import React, { ReactNode, useEffect, useRef, useState } from "react";

// Shared building blocks for the eligibility / enroll lightbox forms:
//  - Section: a titled, bordered card (anchored by a slug id for the legend to scroll to).
//  - ReadField: a read-only label + value (no input box).
//  - SectionNavLayout: a clickable left-hand legend (derived from the Section titles) beside a
//    single scrollable column of cards, with the active section highlighted as you scroll.

const slug = (title: string) =>
  "section-" +
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

export const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div id={slug(title)} className="form-section">
    <div className="form-section__title">{title}</div>
    <div className="row g-3">{children}</div>
  </div>
);

export const ReadField = ({
  label,
  value,
  className = "col-12",
}: {
  label: string;
  value?: string | number | null;
  className?: string;
}) => (
  <div className={className}>
    <div className="text-light fs-11 mb-1">{label}</div>
    <div className="fw-medium fs-14 text-break">
      {value || value === 0 ? value : "-"}
    </div>
  </div>
);

export const SectionNavLayout = ({ children }: { children: ReactNode }) => {
  const items = React.Children.toArray(children)
    .filter(React.isValidElement)
    .map((child) => {
      const title = (child as React.ReactElement<{ title: string }>).props.title;
      return { id: slug(title), title };
    });
  const idsKey = items.map((i) => i.id).join("|");

  const scrollRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<string>(items[0]?.id ?? "");
  const [padBottom, setPadBottom] = useState(0);

  // Pad the bottom by ~one viewport minus the last card so any section (even the last)
  // can be scrolled all the way to the top of the body when its legend item is clicked.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const measure = () => {
      const lastId = idsKey.split("|").pop();
      const last = lastId ? document.getElementById(lastId) : null;
      setPadBottom(Math.max(container.clientHeight - (last?.offsetHeight ?? 0), 0));
    };
    // ResizeObserver fires on observe and again once the modal animates to its final size.
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [idsKey]);

  // Highlight the section currently near the top of the scroll viewport.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { root, rootMargin: "0px 0px -70% 0px", threshold: 0 },
    );
    idsKey.split("|").forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [idsKey]);

  const scrollTo = (id: string) => {
    setActive(id);
    const container = scrollRef.current;
    const el = document.getElementById(id);
    if (!container || !el) return;
    // Scroll within the body so the chosen section sits at the top (keeping the small top gap).
    const padTop = parseFloat(getComputedStyle(container).paddingTop) || 0;
    const top =
      el.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      padTop;
    container.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
  };

  return (
    <div className="flex-fill d-flex overflow-hidden" style={{ minHeight: 0 }}>
      <nav className="form-nav d-none d-lg-flex flex-column flex-shrink-0 border-end p-3 overflow-auto">
        <div className="form-nav__heading text-uppercase text-light fw-semibold fs-11 mb-2 px-2">
          Sections
        </div>
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            className={`form-nav__item ${active === it.id ? "active" : ""}`}
            onClick={() => scrollTo(it.id)}
          >
            {it.title}
          </button>
        ))}
      </nav>
      <div ref={scrollRef} className="form-cards flex-fill overflow-auto px-4 pt-3" style={{ minHeight: 0 }}>
        <div className="d-flex flex-column gap-3" style={{ paddingBottom: padBottom }}>
          {children}
        </div>
      </div>
    </div>
  );
};
